import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { TooltipProvider } from "../components/tooltip/TooltipHost";
import c1_state from "../../ConceptOne/src/model/C1_State";
import { C1_PlanSvg } from "../../ConceptOne/src/plan/C1_PlanSvg";
import { C1_FloorFinishSvgView } from "../../ConceptOne/src/plan/C1_FloorFinishSvgView";
import { C1_WallLayer } from "../../ConceptOne/src/plan/C1_WallLayer";
import { C1_FreeWallsView } from "../../ConceptOne/src/plan/C1_FreeWallsView";
import { C1_OpeningsSvgView } from "../../ConceptOne/src/plan/C1_OpeningsView";
import { C1_FurnitureSvgView } from "../../ConceptOne/src/plan/C1_FurnitureSvgView";
import { C1_EloItemsSvgView } from "../../ConceptOne/src/plan/C1_EloItemsSvgView";
import { C1_RoomDimensions } from "../../ConceptOne/src/plan/C1_RoomDimensions";
import { C1_RoomTextSvgView } from "../../ConceptOne/src/plan/C1_RoomTextSvgView";
import { C1_DimensionLayer } from "../../ConceptOne/src/plan/C1_DimensionLayer";
import { C1_NorthArrow } from "../../ConceptOne/src/plan/C1_NorthArrow";
import { C1_ReferenceFloorView } from "../../ConceptOne/src/plan/C1_ReferenceFloorView";
import "../../ConceptOne/src/C1_styles.scss";

/** Uses the shared plan layers without mounting an editor or registering editing interactions. */
function PlanExportSurface() {
  const floor = c1_state.currentFloor;
  const project = c1_state.project;
  const openings = floor.rooms.flatMap((room) => room.openings);
  const referenceFloor = project.floors.find((candidate) => candidate.visible && candidate.id !== floor.id);
  return (
    <div
      className="C1_canvas C1_floorImageRendering"
      style={{ width: c1_state.canvasWidth, height: c1_state.canvasHeight }}
    >
      <C1_PlanSvg>
        {referenceFloor && <C1_ReferenceFloorView floor={referenceFloor} project={project} layer="background" />}
        <C1_FloorFinishSvgView floor={floor} />
        {referenceFloor && <C1_ReferenceFloorView floor={referenceFloor} project={project} layer="content" />}
        <C1_WallLayer floor={floor} project={project} exterior={false} />
        <C1_WallLayer floor={floor} project={project} exterior />
        <C1_FreeWallsView floor={floor} />
        <C1_OpeningsSvgView openings={openings} onStartDrag={() => {}} onStartOpeningResize={() => {}} />
        <C1_FurnitureSvgView floor={floor} />
        <C1_EloItemsSvgView floor={floor} openings={openings} />
        <C1_RoomDimensions floor={floor} />
        <C1_RoomTextSvgView floor={floor} />
        {c1_state.floorImageRenderOptions.value?.includeDimensions && <C1_DimensionLayer />}
        <C1_NorthArrow />
      </C1_PlanSvg>
    </div>
  );
}

/** Renders a complete floor on demand, independently of the main app's currently open panes. */
export function createPlanExportSurface() {
  const container = document.createElement("div");
  container.className = "C1";
  container.style.cssText = "position:fixed;left:-100000px;top:0;width:1600px;height:1200px;pointer-events:none;";
  container.setAttribute("aria-hidden", "true");
  document.body.appendChild(container);
  const root = createRoot(container);
  return {
    getPlanElement(): HTMLElement {
      // A separate React root does not inherit the app's context providers.
      flushSync(() => root.render(
        <TooltipProvider>
          <PlanExportSurface />
        </TooltipProvider>,
      ));
      return container;
    },
    dispose() {
      root.unmount();
      container.remove();
    },
  };
}
